import AdditionalZone from '@/components/AdditionalZone'
import Button from '@/components/Button'
import SelectorBox2 from '@/components/SelectorBox2'
import Toast2 from '@/components/Toast2'
import useBehalfPanel from '@/hooks/use-behalf-panel'
import useConfig from '@/hooks/use-config'
import usePage from '@/hooks/use-page'
import useTransaction from '@/hooks/use-transaction'
import { useNavigate, useParams } from '@/lib/router-dom/index'
import api_forward from '@/services/api/forward.js'
import "@/translations/i18n"
import rem from '@/utils/rem'
import classname from 'classnames'
import produce from 'immer'
import { useEffect, useState } from 'react'
import { useTranslation } from "react-i18next"
import KeyboardEventHandler from '@infinium/react-keyboard-event-handler'
import Stepper from '@/componentsDS/Stepper/Stepper'

const SharePage = () => {

	/*=================================================
		General
	/================================================*/

	const { t } = useTranslation();
	const [pageTitle, setPageTitle] = useState(t("forwardData"))

	const { breadcrumbsItems, setBreadcrumbsItems } = usePage({ title: pageTitle, breadcrumbs: [t("myPitstop"), pageTitle, t("chooseDataElement")] })
	const { goTo } = useNavigate();

	const { messageId } = useParams()

	/*=================================================
		Error Modal
	/================================================*/
	const [error, setError] = useState()
	function handleError(){
		setError()
	}

	/*=================================================
		API
	/================================================*/

	const { config, identity, provideItems, error: errorConfig } = useConfig()
	const { items, currentItem, view, fetchOne, error: errorTraansation } = useTransaction()

	const [participants, setParticipants] = useState([])
	const [selectedDataElement, setSelectedDataElement] = useState([])
	const [selectedParticipants, setSelectedParticipants] = useState([])
	const [selectedOnBehalf, setSelectedOnBehalf] = useState(null)
	const [onBehalfOf, setOnBehalfOf] = useState([])
	const [referenceId, setReferenceId] = useState('')

	useEffect(() => {
		fetchOne(messageId)
	}, [config])

	useEffect(() => {
		const selectDataElement = provideItems?.filter(item=> item.id === currentItem?.dataElement.id)
		setSelectedDataElement(selectDataElement)
		if (selectDataElement){
			setParticipants(selectDataElement[0]?.to)
		}
	}, [currentItem])

	useEffect(() => {
		setOnBehalfOf(selectedParticipants.reduce((pre, cur) => [...pre, ...cur.on_behalf_of], []))
	}, [selectedParticipants])

	const [ behalfPanel, setBehalfPanel ] = useBehalfPanel(onBehalfOf)

	const ParticipantsList = () => {
		if (!participants) {
			return ''
		}

		const list = participants.map((element, index) => {

			const active = selectedParticipants.find(participant => participant.id === element.id)
			const activeIndex = selectedParticipants.findIndex(participant => participant.id === element.id)

			function toggleParticipant() {
				if (active) {
					const updatedSelectedParticipants = produce(selectedParticipants, draft => {
						draft.splice(activeIndex, 1)
					})
					return setSelectedParticipants(updatedSelectedParticipants)
				}

				const updatedSelectedParticipants = produce(selectedParticipants, draft => {
					draft.splice(activeIndex, 1)
					draft.push(element)
				})
				return setSelectedParticipants(updatedSelectedParticipants)
			}

			return (
				<div className="data-element-selectors__col" key={index} onClick={() => toggleParticipant()}>
					<SelectorBox2 label={element.name} image={element.iconUrl} active={active} />
				</div>
			)
		})

		return list
		// return <></>
	}

	useEffect(()=>{
		setError(errorConfig)
		setError(errorTraansation)
	}, [errorConfig, errorTraansation])

	/*=================================================
		Steps
	/================================================*/
	const stepArr= [
		{stepTitle: t("chooseParticipant")},
		{stepTitle: t("inputDataInformation")}
	]
	const [steps, setSteps] = useState([...stepArr])
	const [step, setStep] = useState(1)

	const [stepOneStatus, setStepOneStatus] = useState(false)
	const [stepTwoStatus, setStepTwoStatus] = useState(true)

	useEffect(() => {

		if (selectedDataElement && selectedDataElement.id) {
			return setStepOneStatus(true)
		}

		setStepOneStatus(false)
	}, [selectedDataElement])

	useEffect(() => {

		const updatedBreadcrumbs = produce(breadcrumbsItems, draft => {
			draft[draft.length - 1] = steps[step - 1].stepTitle
		})
		return setBreadcrumbsItems(updatedBreadcrumbs)

	}, [step])


	function goToStep(step) {
		if (step < 1 || step > steps.length) {
			return
		}
		setStep(step)
	}

	function prevStep() {
		goToStep(step - 1)
	}

	function nextStep() {
		goToStep(step + 1)
	}

	/*=================================================
		Submit
	/================================================*/

	const [submitLoading, setSubmitLoading] = useState()

	async function submit() {

		if (submitLoading) {
			return
		}

		setSubmitLoading(true)

		const myPitstop = selectedParticipants.find(participant => participant.id === 'MY_PITSTOP')
		const othersPitstops = selectedParticipants.filter(participant => participant.id !== 'MY_PITSTOP')

		const allPushPromises = []

		if (othersPitstops.length > 0) {
			allPushPromises.push(pushMessage())
		}

		// For others Pitstops
		function pushMessage() {

			const payload = {
				participants: othersPitstops.map(participant => ({ ...participant, meta: { data_ref_id: referenceId } })),
				on_behalf_of: selectedOnBehalf ? [selectedOnBehalf] : [],
				messageId: currentItem?.id.toString()
			}

			return new Promise(async (resolve, reject) => {

				const [error, data] = await api_forward.forward(selectedDataElement[0]?.id, payload)

				if (error) {
					const { error: errorData } = error
					const errorRow = (errorData?.errors.filter(err => err.errors && err.errors.length > 0).reduce((pre, cur) => ({ errors: [...pre.errors, ...cur.errors?.map(err => ({ i: cur.index + 1, ...err }))] }), { errors: [] }))?.errors?.map((innerError, index) => `${t("item")} ${innerError.i} ${innerError.message}`) ?? [t("unexpectedError")]

					return setError(error?.error?.message || "Failed to forward message.")

					// return reject(errorRow)
				}

				return resolve(data)
			})
		}

		const allSettled = await Promise.allSettled(allPushPromises)
		const allErrors = allSettled.filter(result => result.status === 'rejected')
		const allSuccess = allSettled.filter(result => result.status === 'fulfilled')

		setSubmitLoading(false)

		// if (allSuccess.length === allSettled.length) {
			goTo('/forward/success')
		// }
	}

	/*=================================================
		Routes
	/================================================*/

	function onSelectedOnBehalf(params) {
		if (behalfPanel && params && params.id && params.id !== 'no') {
			setSelectedOnBehalf(params)
			setStepTwoStatus(true)
		} else if (!behalfPanel) {
			setStepTwoStatus(true)
		} else {
			setStepTwoStatus(false)
		}
	}

	function onInputCallback(params) {
		if (params) {
			setReferenceId(params)
		}
	}


	return (
		<>
			<div className="title-header">
				<div className="title-header__left">
					<div className="title-header__title">
						{pageTitle}
					</div>
				</div>

				<div className="title-header__right">

				</div>
			</div>

			<Stepper steps={steps} currentStep={step-1}/>

			<hr />

			{step === 1 && (
				<>
					<span className="t--share-data-subheader">{t("chooseWhomToShare")}</span>
					<div className="data-element-selectors">
						<div className="data-element-selectors__cols">
							<ParticipantsList />
						</div>
					</div>
				</>
			)}

			{step === 2 && (
				<>
					<span className="t--share-data-subheader">{t("uploadYourData")}</span>

					<div className="layout-upload-data">
						<div className="layout-upload-data__left">

							<div className="additional-zone__box">
								<AdditionalZone on_behalf_of={onBehalfOf} flow="share" callback={params => onSelectedOnBehalf(params)} callbackInput={params => onInputCallback(params)} />
							</div>
						</div>

						<div className="layout-upload-data__right">

						</div>
					</div>
				</>
			)}

			<div style={{ height: rem(72) }}></div>
			<div className="DS__layout__footer">
				<div className="layout-default__sticky-actions">
					<KeyboardEventHandler handleKeys={["alt+right", "alt+left", "enter", "esc"]} onKeyEvent={(key, e) => {
						if (key === "alt+left" || key === "esc") {
							prevStep()
						}
						if (key === "alt+right" || key === "enter") {
							if (stepOneStatus && step === 1) {
								nextStep()
							}
							if ((stepTwoStatus && stepTwoStatus) && step === steps.length) {
								submit()
							}
						}
					}} />
					<div className="layout-default__sticky-actions__left">
						{step > 1 &&
							<Button text={t("previous")}
								sizing='Medium' ghost full round maxWidth='154'
								submit={() => prevStep()}
							/>
						}
					</div>
					<div className="layout-default__sticky-actions__right">
						{step === 1 &&
							<Button text={t("next")}
								sizing='Medium' full round maxWidth='154'
								disabled={!stepTwoStatus}
								submit={() => nextStep()}
							/>
						}
						{step === 2 &&
							<Button text={t("submit")}
								sizing='Medium' full round maxWidth='154'
								disabled={!(stepTwoStatus && stepTwoStatus)}
								submit={() => submit()}
								loading={submitLoading}
							/>
						}
					</div>
				</div>
			</div>
			<Toast2 show={error} errorMessage={error} close={()=>{handleError()}} />
		</>
	)
}

export default SharePage
